<template>
<div ref="carousel" class="carousel" v-on:pointerdown="stopAutoAdvancing">
    <div v-if="!isMobile" class="desktop-layout">
        <div class="spotlight">
            <SpotlightSlide
                v-for="(slide, idx) in homepageFeatures"
                v-show="currentSlide === idx"
                :key="slide.slug"
                :slide="slide"
                :platforms="slidePlatforms[slide.slug] || []"
            />
            <div class="controls">
                <div
                    v-for="(slide, idx) in homepageFeatures"
                    :key="idx"
                    role="button"
                    tabIndex="0"
                    class="slide-dot"
                    :class="{'active': currentSlide === idx}"
                    :style="currentSlide === idx ? { backgroundColor: slide.navControlColor } : {}"
                    v-on:click="selectSlide(idx); trackSlide('click-controls')"
                    v-on:keydown.enter="selectSlide(idx); trackSlide('keydown-controls')"
                />
            </div>
            <div class="gradient-right" />
        </div>
        <div class="thumbnails">
            <div
                v-for="(slide, idx) in homepageFeatures"
                :key="idx"
                role="button"
                tabIndex="0"
                class="thumbnail"
                :class="{'active': currentSlide === idx}"
                :style="{ backgroundImage: `url(${slide.thumbnail?.data?.attributes?.url})` }"
                v-on:click="selectSlide(idx); trackSlide('click-thumbnail')"
                v-on:keydown.enter="selectSlide(idx); trackSlide('keydown-thumbnail')"
            />
        </div>
    </div>
    <div v-else class="mobile-layout">
        <div class="cards-container">
            <MobileCard
                v-for="(slide, idx) in homepageFeatures"
                :key="idx"
                :slide="slide"
                :variant="mobileCardVariant"
                :platforms="slidePlatforms[slide.slug] || []"
            />
        </div>
        <div class="mobile-controls">
            <div
                v-for="(slide, idx) in homepageFeatures"
                :key="idx"
                role="button"
                tabIndex="0"
                class="mobile-dot"
                :class="{'active': currentSlide === idx}"
                v-on:click="selectSlide(idx); trackSlide('click-controls-mobile')"
                v-on:keydown.enter="selectSlide(idx); trackSlide('keydown-controls-mobile')"
            />
        </div>
    </div>
</div>
</template>

<script setup lang="ts">
import { Shopify } from '@jackboxgames/shopify'
import type { JBGWeb } from '$types/JBGWeb'
import SpotlightSlide from './SpotlightSlide.vue'
import MobileCard from './MobileCard.vue'
import { currentCountry } from '~/services/shopify'
import { getStorefrontsFromProductData } from '../../services/storefronts/storefronts'
import { LaunchDarkly } from '$services/launchdarkly'
import Metric = LaunchDarkly.Metric

const app = useNuxtApp()
const carousel = ref<HTMLDivElement>(null)
const currentSlide = ref(0)
const slideIntervalId = ref<number>()
const isMobile = ref(false)
const isManuallyAdvancing = ref(false)
const observer = ref<IntersectionObserver | null>(null)
const slidePlatforms = ref<Record<string, JBGWeb.PlatformOption[] | void>>({})

const populate = [
    'packDetail',
    'packDetail.wordmark',
    'externalGame',
    'externalGame.wordmark',
    'gameDetail',
    'gameDetail.wordmark',
    'bundleDetail',
    'bundleDetail.wordmark',
    'spotlightWide',
    'spotlightMobile',
    'thumbnail',
    'cta'
]

const { data: homepageFeatures } = await useAsyncData<JBGWeb.HomepageFeature[]>(
    'homepage-features',
    async () => {
        try {
            let features = await app.$cms.fetchMany<JBGWeb.HomepageFeature[]>('homepage-features', {
                populate,
                filters: {
                    enabled: { $eq: true }
                },
                locale: app.$i18n.locale.value
            })

            // if no slides are found and the language is not english, do a second fetch in english
            if (features.length === 0 && app.$i18n.locale !== 'en') {
                features = await app.$cms.fetchMany<JBGWeb.HomepageFeature[]>('homepage-features', {
                    populate,
                    filters: {
                        enabled: { $eq: true }
                    },
                    locale: 'en'
                })
            }

            return features?.sort((a, b) => a.sortOrder - b.sortOrder)
        } catch (err: any) {
            console.error('Failed to fetch features:', err.message)
            return null
        }
    }
)

const numSlides = computed(() => homepageFeatures.value?.length || 0)
const currentSlideData = computed(() => homepageFeatures.value[currentSlide.value])

// TODO: eventually add an external games fetch once we start adding those to shopify
async function fetchShopifyProductForSlide(slide: JBGWeb.HomepageFeature) {
    let data
    try {
        if (slide.type === 'pack' && !slide.packDetail.data.attributes.displayWishlistUI) {
            data = await app.$cms.fetchOne<JBGWeb.PackDetail>('pack-details', slide.slug, {
                populate: ['shopifyProduct'],
                locale: 'en',
                countryCode: currentCountry.code
            })
        } else if (slide.type === 'game' && !slide.gameDetail.data.attributes.displayWishlistUI) {
            data = await app.$cms.fetchOne<JBGWeb.GameDetail>('game-details', slide.slug, {
                populate: ['shopifyProduct'],
                locale: 'en',
                countryCode: currentCountry.code
            })
        } else if (slide.type === 'bundle' && !slide.bundleDetail.data.attributes.displayWishlistUI) {
            data = await app.$cms.fetchOne<JBGWeb.BundleDetail>('bundle-details', slide.slug, {
                populate: ['shopifyProduct'],
                locale: 'en',
                countryCode: currentCountry.code
            })
        }

        const product: Shopify.Product = data?.shopifyProduct || null
        slidePlatforms.value[slide.slug] = getStorefrontsFromProductData(product)
    } catch (error) {
        console.error(`Failed to fetch Shopify product for ${slide.slug}:`, error)
        slidePlatforms.value[slide.slug] = []
        return []
    }
}

// TODO: eventually add an external games fetch once we start adding those to shopify
async function fetchAllShopifyProducts() {
    const slidesToFetch = homepageFeatures.value.filter((slide) => ['pack', 'game', 'bundle'].includes(slide.type))
    await Promise.all(
        slidesToFetch.map((slide) => fetchShopifyProductForSlide(slide))
    )
}

let mobileCardVariant = ref('variant-A')

function updateIsMobile() {
    isMobile.value = window.innerWidth <= 850
}

function stopAutoAdvancing() {
    window.clearInterval(slideIntervalId.value)
}

async function centerMobileSlide(container: Element, el: HTMLElement) {
    let finishedScrolling: () => void
    const scrollingPromise = new Promise<void>((res) => {
        finishedScrolling = res
    })

    container.addEventListener('scrollend', () => {
        finishedScrolling()
    }, { once: true })

    el.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
    })

    return scrollingPromise
}

async function selectSlide(idx: number) {
    currentSlide.value = idx

    if (isMobile.value && carousel.value) {
        const cardsContainer = carousel.value.querySelector('.cards-container')
        const cards = cardsContainer?.querySelectorAll('.mobile-card')
        if (cards && cards[idx]) {
            isManuallyAdvancing.value = true
            await centerMobileSlide(cardsContainer, cards[idx] as HTMLElement)
            isManuallyAdvancing.value = false
        }
    }
}

function setupMobileObserver() {
    if (!carousel.value) return

    const cardsContainer = carousel.value.querySelector('.cards-container')
    if (!cardsContainer) return

    const features = Array.from(cardsContainer.children)
    observer.value = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!isManuallyAdvancing.value && entry.isIntersecting) {
                const feature = entry.target
                currentSlide.value = features.indexOf(feature)
            }
        })
    }, {
        root: cardsContainer,
        threshold: 0.75
    })

    features.forEach((feature) => observer.value.observe(feature))
}

// watch screen resizing to ensure correct card handling
watch(isMobile, async (mobile) => {
    if (mobile) {
        await nextTick()
        setupMobileObserver()
    } else {
        observer.value.disconnect()
    }
})

onMounted(async () => {
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
    setupMobileObserver()
    trackSlide('default')
    mobileCardVariant = ref(app.$launchDarkly.useFlag('homepage-mobile-carousel-slide', 'variant-A'))

    try {
        await fetchAllShopifyProducts()
    } catch (error) {
        console.log(error)
    }

    // auto advance
    slideIntervalId.value = window.setInterval(() => {
        let slide = currentSlide.value + 1
        if (slide >= numSlides.value) {
            slide = 0
        }
        void selectSlide(slide).then(() => {
            trackSlide('auto-advance')
        })
    }, 8000)
})

onUnmounted(() => {
    stopAutoAdvancing()
    window.removeEventListener('resize', updateIsMobile)
})

function trackSlide(interaction: string) {
    const app = useNuxtApp()
    app.$launchDarkly.track(Metric.CarouselSlide, {
        slide: currentSlideData.value.slug,
        slideNum: currentSlide.value,
        interaction
    })
}

</script>

<style lang="scss" scoped>
@import "../../styles/kit.scss";

.carousel {

    .desktop-layout {
        display: flex;
        align-items: stretch;
        gap: 20px;
        max-width: 1312px;
        margin: 24px auto;
        padding-left: 16px;
        padding-right: 16px;

        .spotlight {
            position: relative;
            overflow: hidden;
            flex: 1;
            min-width: 0;
            max-width: 1097px;
            border-radius: 16px;
            z-index: 1;

            .controls {
                position: absolute;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                gap: 8px;
                right: 20px;
                bottom: 20px;
                z-index: 3;

                .slide-dot {
                    display: inline-block;
                    width: 18px;
                    height: 14px;
                    background: var(--x-ink-100);
                    border-radius: 4px;
                    transition: background 0.2s ease, opacity 0.2s ease;
                    opacity: 0.6;

                    &:hover {
                        cursor: pointer;
                        background: rgba(255, 255, 255, 0.5);
                        transform: scale(1.1);
                    }

                    &.active {
                        opacity: 1;
                    }
                }
            }

            .gradient-right {
                position: absolute;
                pointer-events: none;
                right: 0px;
                bottom: 0px;
                width: 50%;
                height: 50%;
                border-radius: 999px 0 0 0;
                z-index: 2;
                background:radial-gradient(
                    70.78% 70.71% at 100% 100%,
                    rgba(0, 0, 0, 0.64) 0%,
                    rgba(0, 0, 0, 0.32) 50%,
                    rgba(0, 0, 0, 0.00) 100%);
            }
        }

        .thumbnails {
            display: flex;
            flex-direction: column;
            width: 160px;
            gap: 16px;

            @include mq-medium {
                width: 150px;
            }

            @include mq-small {
                width: 110px;
            }

            .thumbnail {
                background-size: cover;
                background-position: center;
                background-repeat: no-repeat;
                object-fit: cover;
                flex: 1;
                border-radius: 14px;
                opacity: 0.6;

                &:hover {
                    opacity: 0.85;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
                    cursor: pointer;
                }

                &.active {
                    opacity: 1;
                }
            }
        }

    }

    .mobile-layout {
        margin: 16px 0 16px 16px;

        .cards-container {
            display: flex;
            gap: 16px;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            scroll-behavior: smooth;
            padding-bottom: 16px;
            -webkit-overflow-scrolling: touch;

            // Hide scrollbar only on touch devices
            @media (max-width: 850px) {
                scrollbar-width: none;

                &::-webkit-scrollbar {
                    display: none;
                }
            }

            // Show scrollbar on devices with mouse/trackpad
            @media (max-width: 850px) and (pointer: fine) {
                scrollbar-width: auto;

                &::-webkit-scrollbar {
                    display: block;
                    height: 8px;
                    cursor: pointer;
                }

                &::-webkit-scrollbar-thumb {
                    background: #48484b;
                    border-radius: 4px;
                    cursor: pointer;
                }

                &::-webkit-scrollbar-track {
                    background: transparent;
                }
            }
        }

        .mobile-controls {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 8px;

            // Hide controls on devices with mouse/trackpad
            @media (max-width: 850px) and (pointer: fine) {
                display: none;
            }

            .mobile-dot {
                width: 15px;
                height: 12px;
                background: var(--x-ink-200);
                border-radius: 2px;
                transition: background 0.2s ease, opacity 0.2s ease;
                opacity: 0.6;
                cursor: pointer;

                &:hover {
                    cursor: pointer;
                    background: rgba(255, 255, 255, 0.5);
                    transform: scale(1.1);
                }

                &.active {
                    background: #F3F3FD;
                    opacity: 1;
                }
            }
        }

    }
}
</style>
